import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { useCallback, useContext, useEffect, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router";
import FormNumberInput from "../../../components/FormNumberInput";
import NoInfo from "../../../components/NoInfo";
import PrimePanel from "../../../components/PrimePanel";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import NumberUtil from "../../../utility/NumberUtil";
import { F106031001Before, queryCbamPrecursor, updateCbamPrecursor } from "../../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import { Badge } from "primereact/badge";
import { BigNumber } from "bignumber.js";

const PrecursorInfo = ({ setActiveIndex }) => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    currentCompany,
    setCurrentCompany,
    currentCbamMasterSeq,
    setCurrentCbamMasterSeq,
  } = useOutletContext();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const [oriData, setOriData] = useState({});
  const [dataSourceList, setDataSourceList] = useState([]);
  const [coefficientSourceList, setCoefficientSourceList] = useState([]);
  const isEditable = useEditableFlag("F10603");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    handleSubmit,
    clearErrors,
    trigger,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const { fields: cbamPurchPrecFields } = useFieldArray({
    control,
    name: "cbamPurchPrecList",
  });

  useEffect(() => {
    axios_F106031001Before();
    axios_queryCbamPrecursor();
    axios_getCBAMSubItemsByItemNo("COEFFIC_SOURCE", 16).then(setCoefficientSourceList);
    axios_getCBAMSubItemsByItemNo("DATA_SOURCE", 17).then(setDataSourceList);
  }, []);

  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidPurchPrecForm = useCallback(
    (formId) => {
      if (setValidForm("cbamPurchPrecList")?.length > 0) {
        const tmp = setValidForm("cbamPurchPrecList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamPurchPrecList")],
  );

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const axios_F106031001Before = () => {
    checkLoading(loading);
    F106031001Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // D_Processes 表 查詢
  const axios_queryCbamPrecursor = () => {
    checkLoading(loading);
    queryCbamPrecursor({
      companySeq: currentCompany?.companySeq,
      cbamMasterSeq: currentCbamMasterSeq,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          const oriData = {
            cbamPurchPrecList: res?.data.RespBody?.cbamPurchPrecList.map((item) => {
              const newPurchPrecdList = item.cbamPurchPrecdList.map((x) =>
                x.refType === "cbamRouteData" && x.routeId === "all" ? { ...x, displayName: t("allProduct") } : x,
              );

              return {
                ...item,
                cbamPurchPrecdList: newPurchPrecdList,
                paramList: [
                  {
                    field: "seeDirectEmissions",
                    name: t("seeDirectEmissions"),
                    value: item.seeDirectEmissions,
                    src: item.seeDirectSrc,
                  },
                  {
                    field: "seeElectricityConsumption",
                    name: t("seeElectricityConsumption"),
                    value: item.seeElectricityConsumption,
                    src: item.seeElectricitySrc,
                  },
                  {
                    field: "electricityFactor",
                    name: t("electricityEmissionFactor"),
                    value: item.electricityFactor,
                    src: item.electricityFactorSrc,
                  },
                  {
                    field: "seeIndirectEmissions",
                    name: t("seeIndirectEmissions"),
                    value: item.seeIndirectEmissions,
                    editFlag: false,
                  },
                ],
              };
            }),
          };
          setOriData(oriData);
          Object.entries(oriData).forEach(([key, value]) => {
            setValue(key, value);
          });
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const nonEditableCell = (value, type = "text") => {
    const safeValue = value ?? "";
    const valueDisplay = type === "number" ? NumberUtil.formatWithCommas(safeValue) || "" : safeValue;
    return <div className={`non-editable-cell ${type === "number" ? "right" : ""}`}>{valueDisplay}</div>;
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = (data) => {
    checkLoading(loading);
    const coefficientSourceMap = new Map(coefficientSourceList.map((v, i) => [v.subitemNo, v.index]));
    const dataSourceMap = new Map(dataSourceList.map((v, i) => [v.subitemNo, v.index]));

    const reqBody = {
      cbamPurchPrecList: data.cbamPurchPrecList.map((item) => {
        const cbamPurchPrec = {};

        item.paramList.forEach((param) => {
          switch (param.field) {
            case "seeDirectEmissions":
              cbamPurchPrec.seeDirectEmissions = param.value;
              cbamPurchPrec.seeDirectSrc = encoder(dataSourceMap, param.src);
              break;
            case "seeElectricityConsumption":
              cbamPurchPrec.seeElectricityConsumption = param.value;
              cbamPurchPrec.seeElectricitySrc = encoder(dataSourceMap, param.src);
              break;
            case "electricityFactor":
              cbamPurchPrec.electricityFactor = param.value;
              cbamPurchPrec.electricityFactorSrc = encoder(coefficientSourceMap, param.src);
              break;
            case "seeIndirectEmissions":
              cbamPurchPrec.seeIndirectEmissions = param.value;
              break;
          }
        });

        return {
          cbamPurchPrecSeq: item.cbamPurchPrecSeq,
          cbamMasterSeq: item.cbamMasterSeq,
          cbamPrecursorsDataSeq: item.cbamPrecursorsDataSeq,
          dataId: item.dataId,
          ...cbamPurchPrec,
          cbamPurchPrecdList: item.cbamPurchPrecdList,
        };
      }),
    };

    updateCbamPrecursor(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          setActiveIndex(6);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const HeaderTemplate = ({ options, field, index }) => {
    return (
      <div className={`${options.className}`} key={field.cbamDetailSeq}>
        <div className="flex w-full font-bold align-items-center">
          <Badge value={index + 1} style={{ marginRight: "0.5rem" }} />
          {t("precursor")} {field.displayName ? " : " + field.displayName : ""}
        </div>
        {options.togglerElement}
      </div>
    );
  };

  return (
    <>
      {cbamPurchPrecFields.length === 0 && <NoInfo />}

      <form className="grid" onSubmit={handleSubmit(onSubmit)}>
        {cbamPurchPrecFields.map((field, index) => {
          const consumptionSum = (watch(`cbamPurchPrecList.${index}.cbamPurchPrecdList`) || [])
            .filter((item) => item.refType === "cbamRouteData")
            .reduce((acc, item) => acc.plus(new BigNumber(item.productionProcessConsumption || 0)), new BigNumber(0));

          const otherSum = (watch(`cbamPurchPrecList.${index}.cbamPurchPrecdList`) || [])
            .filter((item) => item.refType === "cbamProcessData" || item.refType === "nonCbam")
            .reduce((acc, item) => acc.plus(new BigNumber(item.productionProcessConsumption || 0)), new BigNumber(0));

          return (
            <div key={field.id} className="col-12">
              <PrimePanel
                key={field.id}
                toggleable
                className="evaluation-temp-setting-panel dataTable-panel"
                headerTemplate={(options) => <HeaderTemplate options={options} field={field} index={index} />}
              >
                <div className="col-12">
                  <DataTable
                    showGridlines
                    scrollable
                    scrollHeight="35rem"
                    stripedRows
                    resizableColumns
                    className={"white-data-table hover-highlight no-shadow"}
                    value={getValues(`cbamPurchPrecList.${index}.cbamPurchPrecdList`)}
                    emptyMessage={<NoInfo panel="table" />}
                    rowGroupMode="rowspan"
                    groupRowsBy={["refType"]}
                  >
                    <Column
                      field={"refType"}
                      style={{ minWidth: "15rem", maxWidth: "15rem" }}
                      body={(rowData) => {
                        if (rowData.refType === "nonCbam") {
                          return <></>;
                        } else {
                          return <>{`${t(rowData.refType)}`}</>;
                        }
                      }}
                    />
                    <Column
                      header={t("evaluationTemplateName")}
                      field={"displayName"}
                      style={{ minWidth: "15rem", maxWidth: "15rem" }}
                      body={(rowData) => {
                        if (rowData.refType === "nonCbam") {
                          return <>{`${t(rowData.refType)}`}</>;
                        } else {
                          return <>{rowData.displayName}</>;
                        }
                      }}
                    />
                    <Column
                      header={`${t("consumption")}(t)`}
                      field={"productionProcessConsumption"}
                      style={{ minWidth: "15rem", maxWidth: "15rem" }}
                      body={(rowData, options) =>
                        editFlag ? (
                          <div className="text-right">{rowData.productionProcessConsumption}</div>
                        ) : (
                          <FormNumberInput
                            disabled={editFlag}
                            register={register}
                            errors={errors}
                            id={`cbamPurchPrecList.${index}.cbamPurchPrecdList.${options.rowIndex}.productionProcessConsumption`}
                            name={`cbamPurchPrecList.${index}.cbamPurchPrecdList.${options.rowIndex}.productionProcessConsumption`}
                            value={watch(
                              `cbamPurchPrecList.${index}.cbamPurchPrecdList.${options.rowIndex}.productionProcessConsumption`,
                            )}
                            onChange={(newValue) =>
                              setValue(
                                `cbamPurchPrecList.${index}.cbamPurchPrecdList.${options.rowIndex}.productionProcessConsumption`,
                                newValue,
                              )
                            }
                          />
                        )
                      }
                    />
                  </DataTable>

                  {!consumptionSum.isEqualTo(otherSum) && (
                    <div className="w-full text-right">
                      <small className="p-error pt-1">
                        {t("cbamPrecursorErrorMsg", { consumptionSum: consumptionSum, otherSum: otherSum })}
                      </small>
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <DataTable
                    showGridlines
                    scrollable
                    scrollHeight="35rem"
                    stripedRows
                    className={"white-data-table hover-highlight no-shadow"}
                    value={getValues(`cbamPurchPrecList.${index}.paramList`)}
                    emptyMessage={<NoInfo panel="table" />}
                    rowGroupMode="rowspan"
                    groupRowsBy={["refType"]}
                  >
                    <Column field={"name"} style={{ minWidth: "15rem", maxWidth: "15rem" }} />
                    <Column
                      header={t("carbonFootprintValue")}
                      field={"value"}
                      style={{ minWidth: "15rem", maxWidth: "15rem" }}
                      body={(rowData, options) => {
                        if (!(rowData?.editFlag ?? true)) {
                          return nonEditableCell(
                            watch(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.value`),
                            "number",
                          );
                        }

                        return editFlag ? (
                          <div className="text-right">{rowData.value}</div>
                        ) : (
                          <FormNumberInput
                            disabled={editFlag}
                            register={register}
                            errors={errors}
                            id={`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.value`}
                            name={`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.value`}
                            value={watch(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.value`)}
                            onChange={(newValue) => {
                              if (
                                getValues(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.field`) ===
                                "seeElectricityConsumption"
                              ) {
                                const newValue2 =
                                  Number(newValue) * Number(getValues(`cbamPurchPrecList.${index}.paramList.2.value`));
                                setValue(`cbamPurchPrecList.${index}.paramList.3.value`, newValue2);
                              }
                              if (
                                getValues(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.field`) ===
                                "electricityFactor"
                              ) {
                                const newValue2 =
                                  Number(newValue) * Number(getValues(`cbamPurchPrecList.${index}.paramList.1.value`));
                                setValue(`cbamPurchPrecList.${index}.paramList.3.value`, newValue2);
                              }
                              setValue(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.value`, newValue);
                            }}
                          />
                        );
                      }}
                    />
                    <Column
                      header={t("data_Source")}
                      field={"src"}
                      style={{ minWidth: "15rem", maxWidth: "15rem" }}
                      body={(rowData, options) => {
                        if (!(rowData?.editFlag ?? true)) {
                          return nonEditableCell("");
                        }
                        const optionsList =
                          getValues(`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.field`) ===
                          "electricityFactor"
                            ? coefficientSourceList
                            : dataSourceList;

                        const value = optionsList.find((item) => item.subitemNo === rowData.src)?.subitemName;
                        return editFlag ? (
                          <div>{value}</div>
                        ) : (
                          <Controller
                            name={`cbamPurchPrecList.${index}.paramList.${options.rowIndex}.src`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <>
                                <Dropdown
                                  disabled={editFlag}
                                  filter
                                  showClear
                                  optionLabel="subitemName"
                                  optionValue="subitemNo"
                                  value={field.value}
                                  virtualScrollerOptions={{ itemSize: 40 }}
                                  defaultValue=""
                                  placeholder={t("plz_chs")}
                                  control={control}
                                  options={optionsList}
                                  className="w-full"
                                  onChange={(event) => field.onChange(event.value)}
                                />
                                {fieldState.error && (
                                  <small className="p-error block mt-1">{fieldState.error.message}</small>
                                )}
                              </>
                            )}
                          />
                        );
                      }}
                    />
                  </DataTable>
                </div>
              </PrimePanel>
            </div>
          );
        })}

        {cbamPurchPrecFields.length > 0 && (
          <div className="col-12 gap-2 flex-all-center">
            {tabAction === "detail" && (
              <Button
                disabled={!isEditable}
                type="button"
                label={t("button.edit")}
                size="small"
                onClick={() => {
                  setTabAction("create");
                }}
              />
            )}
            {tabAction === "create" && (
              <>
                <Button
                  disabled={!(isValid && isDirty)}
                  type="submit"
                  label={action === "create" ? t("button.create") : t("button.update")}
                  size="small"
                />
                {action !== "create" && (
                  <Button
                    outlined
                    size="small"
                    label={t("button.cancel")}
                    onClick={(e) => {
                      e.preventDefault();
                      clearErrors();
                      setTabAction("detail");
                      reset(oriData);
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </form>
    </>
  );
};

export default PrecursorInfo;
